import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router";
import { Typography } from "ui-kit/Typography";
import { FormattedMessage } from "react-intl";
import { Button, CreateReleaseButton } from "ui-kit/Button";
import { Routes } from "constants/routes";
import { DeviceSize } from "constants/deviceSize";
import { InfoModal } from "./InfoModal";
import GeneralMain from "../../assets/general-main.webp";
import GeneralMainMobile from "../../assets/general-mob.webp";
import { StepMore } from "./types";

export const Welcome: React.FC = () => {
  const [openStep, setOpenStep] = useState<StepMore | null>(null);
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);

  const onMore = (step: StepMore) => () => setOpenStep(step);

  const onNext = () => {
    if (openStep === StepMore.Upload) {
      return setOpenStep(StepMore.Create);
    }

    return setOpenStep(StepMore.Receive);
  };

  const onCreateRelease = () => {
    localStorage.setItem("type", "");
    localStorage.setItem("newReleaseFrom", "general");
    navigate(Routes.NewRelease.replace(":id?", ""));
  };

  const onGenerateMusic = () => {
    navigate(Routes.AICompose);
  };

  useEffect(() => {
    const tabletPx = parseInt(DeviceSize.TabletMedium, 10) || 768;
    const handleResize = () => {
      if (typeof window === "undefined") return;
      setIsMobile(window.innerWidth <= tabletPx);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  return (
    <Container>
      <Info>
        <Typography variant="title1">
          <Title1Box>
          <FormattedMessage
            id="Welcome.title"
            defaultMessage="Welcome to Slash Music"
            description="Welcome to Slash Music"
          />
          </Title1Box>
        </Typography>
        <Typography variant="body1">
          <SubtitleBox>
            <div>
          <FormattedMessage
            id="Welcome.subtitle"
            defaultMessage="Here, we make music distribution simple, so you can focus on what truly matters — creating."
            description="subtitle"
          />
          </div>
          <div>
           <FormattedMessage
            id="Welcome.subtitle1"
            defaultMessage="Our expert team ensures your music is delivered quickly and effectively to all major platforms, helping you"
            description="subtitle1"
          />
          </div>
          <div>
           <FormattedMessage
            id="Welcome.subtitle2"
            defaultMessage="reach a global audience. Together, we make sharing your art online seamless"
            description="subtitle2"
          />
          </div>
          </SubtitleBox>
        </Typography>
      </Info>
      <Cards>
        <Card>
          <CardText variant="body2">
            <BlueText>
                <FormattedMessage
                  id="Welcome.Card.Upload"
                  defaultMessage="Upload"
                  description="Upload"
                />
              </BlueText>
            &nbsp;
            <HeadText>
            <FormattedMessage
              id="Welcome.Card.UploadText"
              defaultMessage="your music and submit it for moderation"
              description="your music and submit it for moderation"
            />
            </HeadText>
          </CardText>
          <MoreButton onClick={onMore(StepMore.Upload)}>
            <FormattedMessage
              id="Welcome.Card.MoreButton"
              defaultMessage="More"
              description="More"
            />
          </MoreButton>
        </Card>
        <svg width="26" height="8" viewBox="0 0 26 8" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M25.3536 4.35355C25.5488 4.15829 25.5488 3.84171 25.3536 3.64645L22.1716 0.464466C21.9763 0.269204 21.6597 0.269204 21.4645 0.464466C21.2692 0.659728 21.2692 0.976311 21.4645 1.17157L24.2929 4L21.4645 6.82843C21.2692 7.02369 21.2692 7.34027 21.4645 7.53553C21.6597 7.7308 21.9763 7.7308 22.1716 7.53553L25.3536 4.35355ZM0 4.5H25V3.5H0V4.5Z" fill="#6777FF" />
        </svg>
        <Card>
          <CardText variant="body2">
          <BlueText>
                <FormattedMessage
                  id="Welcome.Card.Create"
                  defaultMessage="Create"
                  description="Create"
                />
              </BlueText>
            &nbsp;
            <HeadText>
            <FormattedMessage
              id="Welcome.Card.CreateText"
              defaultMessage="a multi-link and track your stats"
              description="a multi-link and track your stats"
            />
            </HeadText>
          </CardText>
          <MoreButton onClick={onMore(StepMore.Create)}>
            <FormattedMessage
              id="Welcome.Card.MoreButton"
              defaultMessage="More"
              description="More"
            />
          </MoreButton>
        </Card>
        <svg width="26" height="8" viewBox="0 0 26 8" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M25.3536 4.35355C25.5488 4.15829 25.5488 3.84171 25.3536 3.64645L22.1716 0.464466C21.9763 0.269204 21.6597 0.269204 21.4645 0.464466C21.2692 0.659728 21.2692 0.976311 21.4645 1.17157L24.2929 4L21.4645 6.82843C21.2692 7.02369 21.2692 7.34027 21.4645 7.53553C21.6597 7.7308 21.9763 7.7308 22.1716 7.53553L25.3536 4.35355ZM0 4.5H25V3.5H0V4.5Z" fill="#6777FF" />
        </svg>
        <Card>
          <CardText variant="body2">
          <BlueText>
                <FormattedMessage
                  id="Welcome.Card.Receive"
                  defaultMessage="Receive"
                  description="Receive"
                />
              </BlueText>
            &nbsp;
            <HeadText>
            <FormattedMessage
              id="Welcome.Card.ReceiveText"
              defaultMessage="quarterly reports, royalties, and withdraw earnings"
              description="quarterly reports, royalties, and withdraw earnings"
            />
            </HeadText>
          </CardText>
          <MoreButton onClick={onMore(StepMore.Receive)}>
            <FormattedMessage
              id="Welcome.Card.MoreButton"
              defaultMessage="More"
              description="More"
            />
          </MoreButton>
        </Card>
      </Cards>
      <CreateButtonRow>
        <GenerateMusicButton onClick={onGenerateMusic}>
          <FormattedMessage id="Welcome.GenerateMusic" defaultMessage="Generate music" />
        </GenerateMusicButton>
        <HeroCreateButton onClick={onCreateRelease}>
          <FormattedMessage id="Welcome.CreateRelease" defaultMessage="Create a release" />
        </HeroCreateButton>
      </CreateButtonRow>
      <InfoModal
        isOpen={!!openStep}
        openStep={openStep}
        onHide={() => setOpenStep(null)}
        onNext={onNext}
        onCreateRelease={onCreateRelease}
        onStepChange={setOpenStep}
      />
    </Container>
  );
};

const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  gap: 50px;
  padding: 50px;
  height: 100%;
  box-sizing: border-box;
  overflow: hidden;
  background: url(${GeneralMain}) center/cover no-repeat;

  @media (max-width: ${DeviceSize.TabletMedium}) {
    padding: 20px 16px 24px;
    gap: 24px;
    align-items: flex-start;
    height: 100%;
    min-height: 0;
    background: url(${GeneralMainMobile}) center/cover no-repeat;
  }
`;

const BackgroundImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  pointer-events: none;
  user-select: none;
  z-index: -2;
  filter: brightness(0.92);
`;

const Info = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  max-width: 520px;

  @media (max-width: ${DeviceSize.TabletMedium}) {
    max-width: 100%;
  }
`;

const BlueText = styled.span`
  font-family: SF Pro Display;
  font-weight: 800;
  font-style: Semibold;
  font-size: 17px;
  leading-trim: NONE;
  line-height: 20px;
  letter-spacing: 0%;
  vertical-align: middle;
  color: rgb(255, 255, 255);
`;
const Cards = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;

  & > svg {
    flex-shrink: 0;
  }

  @media (max-width: ${DeviceSize.TabletMedium}) {
    display: none;
  }
`;

const Card = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-end;
  width: 20vw;
  height: 15vh;
  border-radius: 18px;
  padding: 18px 16px;
  overflow: hidden;
  /* Glassmorphism background – needs both standard and -webkit prefix for Safari */
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  background: rgba(208, 218, 240, 0.1);
  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: radial-gradient(
        circle at top left,
        rgba(148, 163, 253, 0.32),
        transparent 55%
      ),
      radial-gradient(
        circle at bottom right,
        rgba(249, 115, 129, 0.32),
        transparent 60%
      );
    filter: blur(18px);
    opacity: 0.9;
    z-index: -2;
  }

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: rgba(17, 24, 39, 0.46);
    /* Extra blur layer under content */
    backdrop-filter: blur(18px);
    -webkit-backdrop-filter: blur(18px);
    z-index: -1;
  }

  @media (max-width: ${DeviceSize.TabletMedium}) {
    width: 100%;
    height: auto;
    align-items: flex-start;
  }
`;

const CardText = styled(Typography)`
  text-align: left;
  line-height: 1.6;
  color: #f9fafb;
  width: 100%;
`;

const MoreButton = styled.a`
  width: fit-content;
  text-decoration: underline;
  cursor: pointer;
  color: ${({ theme }) => theme.palette.text.link};

  @media (max-width: ${DeviceSize.TabletMedium}) {
    align-self: flex-start;
    margin-top: 8px;
  }
`;

const Title1Box = styled.div`
  font-family: Outfit;
  font-weight: 500;
  font-style: Medium;
  font-size: 3.5vh;
  leading-trim: NONE;
  line-height: 33px;
  letter-spacing: 0%;
  vertical-align: middle;
  max-width: 100vw;
  
` 

const SubtitleBox = styled.div`
  font-family: SF Pro Display;
  font-weight: 400;
  font-style: Regular;
  font-size: 2.5vh;
  leading-trim: NONE;
  line-height: 32px;
  letter-spacing: 0%;
  text-align: left;
  vertical-align: middle;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  max-width: 100vw;

  @media (max-width: ${DeviceSize.TabletMedium}) {
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    background: rgba(166, 166, 166, 0.35);
    border-radius: 16px;
    padding: 10px 12px;
  }
`;

const HeadText = styled.span`
  font-family: SF Pro Display;
  font-weight: 400;
  font-style: Regular;
  font-size: 16px;
  leading-trim: NONE;
  line-height: 20px;
  letter-spacing: 0%;
  vertical-align: middle;
`;

const CreateButtonRow = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;

  @media (max-width: ${DeviceSize.TabletMedium}) {
    flex-direction: column;
    align-items: stretch;
    justify-content: flex-end;
    margin-top: auto;
    padding-bottom: 24px;
    gap: 12px;
  }
`;

const HeroCreateButton = styled(CreateReleaseButton)`
  padding: 2rem 5rem;
  font-size: 30px;
  border-radius: 999px;

  @media (max-width: ${DeviceSize.TabletMedium}) {
    width: 100%;
    padding: 1rem 1.5rem;
    font-size: 18px;
  }
`;

const GenerateMusicButton = styled(CreateReleaseButton)`
  padding: 2rem 5rem;
  font-size: 30px;
  border-radius: 999px;
  background: linear-gradient(90deg, #7987FF 0%, #FF7B7D 100%);
  border: none;

  @media (max-width: ${DeviceSize.TabletMedium}) {
    width: 100%;
    padding: 1rem 1.5rem;
    font-size: 18px;
    margin-bottom: 1vh;
  }
`;